// The page chrome: rehearsal mark, theme pill top-right, and the main column.
//
// Theme is in-memory only. theme.ts applies it to document.documentElement
// and writes nothing to the device.

import { useState, type ReactNode } from "react";
import { FootFab, NovaWordmark, SiteFoot, type FootLink } from "@nova/xp-visitor";
import { DocumentModal } from "../../ui/src/legal/DocumentView.js";
import type { AgreementDocument } from "../../ui/src/legal/documents.js";
import { rehearsalNotice } from "./build/environment.js";
import { BUILD } from "./build/config.js";
import { readEnvironment } from "./build/environment.js";
import { FullscreenControl, fullscreenWanted } from "./fullscreen.js";
import type { PageTheme } from "./theme.js";

const environment = readEnvironment(BUILD.environment);
const rehearsal = rehearsalNotice(environment);
// Read once, at load, because the app rewrites its own path as it goes and the
// question is about how this tablet was opened, not where the flow is now.
const fullscreenAsked = typeof window === "undefined" ? false : fullscreenWanted(window.location.search);

function SunIcon(): ReactNode {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <circle cx="12" cy="12" r="4" fill="currentColor" />
      <path
        d="M12 3v2.5M12 18.5V21M4.93 4.93l1.77 1.77M17.3 17.3l1.77 1.77M3 12h2.5M18.5 12H21M4.93 19.07l1.77-1.77M17.3 6.7l1.77-1.77"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function MoonIcon(): ReactNode {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        d="M15.2 3.1A8.6 8.6 0 1 0 20.9 14 7.1 7.1 0 0 1 15.2 3.1z"
        fill="currentColor"
      />
    </svg>
  );
}

export function ThemeToggle({
  theme,
  onTheme,
}: {
  theme: PageTheme;
  onTheme: (next: PageTheme) => void;
}): ReactNode {
  return (
    <div className="web-theme" data-on={theme} role="group" aria-label="Colour theme">
      <button
        type="button"
        className={theme === "light" ? "is-on" : ""}
        aria-pressed={theme === "light"}
        aria-label="Light"
        onClick={() => onTheme("light")}
      >
        <SunIcon />
      </button>
      <button
        type="button"
        className={theme === "dark" ? "is-on" : ""}
        aria-pressed={theme === "dark"}
        aria-label="Dark"
        onClick={() => onTheme("dark")}
      >
        <MoonIcon />
      </button>
    </div>
  );
}

// The footer's documents, onsite, in the order the footer lists them, found
// by kind as the terms screen finds them: a published bundle may give a
// document any id ("ridgeway-terms" with kind "terms"; Bugbot on #190).
const FOOT_DOCUMENTS: readonly (readonly [label: string, kind: AgreementDocument["kind"]])[] = [
  ["Privacy notice", "privacy_notice"],
  ["Terms", "terms"],
  ["Cookie policy", "cookie_notice"],
];

export function Page({
  theme,
  onTheme,
  fullscreen = false,
  lane = "web",
  onsiteFooter = false,
  webFooter = false,
  footDocuments,
  children,
}: {
  theme: PageTheme;
  onTheme: (next: PageTheme) => void;
  /** Offer the corner full-screen control. A build running on our own screen. */
  fullscreen?: boolean;
  lane?: "web" | "onsite";
  onsiteFooter?: boolean;
  /** Public footer stays in normal flow, one viewport below the start. */
  webFooter?: boolean;
  /**
   * Onsite, the stand's own documents. On a hall tablet a footer link that
   * leaves for the main site is a way out of the app, so each one opens the
   * document here instead, with no public link. "Delete my data" is a page a
   * visitor uses from their own device, and the privacy notice says how.
   */
  footDocuments?: readonly AgreementDocument[] | undefined;
  children: ReactNode;
}): ReactNode {
  const [footOpen, setFootOpen] = useState(false);
  const toggleFooter = (): void => setFootOpen((open) => !open);
  const [footDocument, setFootDocument] = useState<AgreementDocument | null>(null);
  const onsiteLinks: FootLink[] | undefined =
    footDocuments === undefined
      ? undefined
      : FOOT_DOCUMENTS.flatMap(([label, kind]) => {
          const document = footDocuments.find((candidate) => candidate.kind === kind);
          return document === undefined ? [] : [{ label, onClick: () => setFootDocument(document) }];
        });
  // Closed for this page view only: nothing is written to the device, and a
  // reload brings the notice back, which is what a rehearsal should do.
  const [rehearsalClosed, setRehearsalClosed] = useState(false);
  return (
    <div className="web-page xp-root xp-reg" data-lane={lane}>
      {rehearsal === null || rehearsalClosed ? null : (
        <div className="web-rehearsal" role="alert">
          <p>{rehearsal}</p>
          <button
            type="button"
            className="web-rehearsal-x"
            aria-label="Close this notice"
            onClick={() => setRehearsalClosed(true)}
          >
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
              <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>
      )}
      <header className="web-head">
        {/* nova-only: the Client system never shows a Nova mark (visitor-foot.css). */}
        <NovaWordmark className="web-wordmark nova-only" />
        <ThemeToggle theme={theme} onTheme={onTheme} />
      </header>
      <main>{children}</main>
      {webFooter ? (
        <div className="web-foot-flow">
          <SiteFoot />
        </div>
      ) : null}
      {onsiteFooter ? (
        <>
          {footOpen ? (
            <button
              type="button"
              className="web-foot-scrim"
              aria-label="Close the footer"
              tabIndex={-1}
              onClick={() => setFootOpen(false)}
            />
          ) : null}
          <div className="web-foot-panel" data-open={footOpen ? "true" : "false"}>
            <SiteFoot links={onsiteLinks} />
          </div>
          {footDocument === null ? null : (
            <DocumentModal document={footDocument} onClose={() => setFootDocument(null)} publicLink="hide" />
          )}
          {/* Onsite only: on the public page the footer is simply below the
              fold, as the hand-off draws it, and needs no control. */}
          <div className="web-foot-toggle">
            <FootFab open={footOpen} onClick={toggleFooter} />
          </div>
        </>
      ) : null}
      {fullscreen ? <FullscreenControl armed={fullscreenAsked} /> : null}
    </div>
  );
}
